import { FunctionComponent, ReactNode, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { Link } from '@utils/cook/types'
import Blink from '@components/Blink'
import { desktopCSS } from '@measures/responsive'
import useGetPageIdentity from '@hooks/useGetPageIdentity'

export type BundlingHeaderProps = {
  title?: string
  link?: Link
  accentColor?: string
  className?: string
  children?: ReactNode
  ctaText?: string
}

export interface AccentColorStyledProps {
  accentColor?: string
}

export interface HasLinkStyledProps {
  hasLink?: boolean
}

interface StyledBundlingHeaderProps {
  hasChildren?: boolean
}

const StyledBundlingHeader = styled.div<
  AccentColorStyledProps & StyledBundlingHeaderProps
>`
  ${({
    theme: {
      spacing: { spacing8, spacing16 },
      colors: { staticRed500 },
      measures: { stretchWidthOnMobile },
    },
    accentColor,
    hasChildren,
  }) => css`
    display: grid;
    grid-template-columns: auto minmax(0, auto);

    align-items: center;
    box-sizing: border-box;
    ${stretchWidthOnMobile};

    padding: ${spacing8} ${spacing16};
    background-color: ${accentColor ?? staticRed500};
    ${desktopCSS(css`
      margin-left: -15px;
      margin-right: -15px;
    `)}

    ${hasChildren &&
    css`
      > :last-child {
        justify-self: flex-end;
      }
    `}
    > :first-child {
      justify-self: flex-start;
    }
  `}
`

const StyledTitle = styled.h1<HasLinkStyledProps>`
  ${({
    theme: {
      typography: {
        headings: {
          medium: { bundledCSS: mediumHeadingCSS },
        },
      },
      colors: { staticWhite },
      utils: { ellipsis },
    },
  }) => css`
    ${mediumHeadingCSS};
    color: ${staticWhite};
    text-transform: uppercase;
    ${ellipsis('100%', 2)}

    margin: 0;
  `}
`

const StyledLinkTitle = styled(Blink)`
  ${({
    theme: {
      typography: {
        headings: {
          medium: { bundledCSS: mediumHeadingCSS },
        },
      },
      colors: { staticWhite },
      utils: { ellipsis },
    },
  }) => css`
    display: block;
    appearance: none;
    text-decoration: none;

    ${mediumHeadingCSS};
    color: ${staticWhite};
    text-transform: uppercase;
    ${ellipsis('100%', 2)}
  `}
`

const StyledChildrenWrapper = styled.div``

const StyledShowAllLink = styled(Blink)`
  ${({
    theme: {
      typography: {
        headings: {
          xxsmall: { bundledCSS: xxsmallHeadingCSS },
        },
      },
      colors: { staticWhite },
      spacing: { spacing8, spacing16 },
    },
  }) => css`
    text-decoration: none;
    ${xxsmallHeadingCSS};
    color: ${staticWhite};
    margin-left: ${spacing8};

    ${desktopCSS(css`
      margin-left: ${spacing16};
    `)}
  `}
`
const Header: FunctionComponent<BundlingHeaderProps> = (props) => {
  const { title, link, accentColor, className, ctaText } = props
  const hasCustomComponent = !!props.children
  const hasLink = !!link
  const showAllButtonEnabled = hasLink && !hasCustomComponent && !!ctaText
  const { isArticle } = useGetPageIdentity()

  const titleContent = useMemo(
    () => (
      <StyledTitle as={isArticle ? 'span' : 'h1'} hasLink={hasLink}>
        {title}
      </StyledTitle>
    ),
    [title, hasLink, isArticle]
  )

  if (!title) {
    return null
  }
  const hasChildren = showAllButtonEnabled || hasCustomComponent

  return (
    <StyledBundlingHeader
      accentColor={accentColor}
      className={className}
      hasChildren={hasChildren}>
      {hasLink ? (
        <StyledLinkTitle {...link}>{titleContent}</StyledLinkTitle>
      ) : (
        titleContent
      )}
      {showAllButtonEnabled && (
        <StyledShowAllLink {...link}>{ctaText}</StyledShowAllLink>
      )}
      {hasCustomComponent && (
        <StyledChildrenWrapper>{props.children}</StyledChildrenWrapper>
      )}
    </StyledBundlingHeader>
  )
}

export { StyledBundlingHeader }

export default Header
